import React from 'react';
import { ArchiveIcon, BellIcon, HomeIcon, MailIcon, SearchIcon, SettingsIcon, UsersIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { InitialAvatar } from '../InitialAvatar';
import { CURRENT_MEMBER } from '../../data/community';
import { WORDMARK } from '../../data/registration';
export type PortalSection = 'home' | 'people' | 'archive' | 'voices' | 'profile';
const NAV: {
  key: PortalSection;
  label: string;
  icon: typeof HomeIcon;
  hint: string;
}[] = [{
  key: 'home',
  label: 'Home',
  icon: HomeIcon,
  hint: 'Where things stand'
}, {
  key: 'people',
  label: 'People',
  icon: UsersIcon,
  hint: '28 members'
}, {
  key: 'archive',
  label: 'Archive',
  icon: ArchiveIcon,
  hint: 'Photographs, recordings, memories'
}, {
  key: 'voices',
  label: 'Voices',
  icon: MailIcon,
  hint: 'This month’s letter'
}];
interface AppShellProps {
  section: PortalSection;
  onNavigate: (section: PortalSection) => void;
  onSignOut: () => void;
  children: React.ReactNode;
}
export function AppShell({
  section,
  onNavigate,
  onSignOut,
  children
}: AppShellProps) {
  return <div className="min-h-screen w-full bg-canvas lg:grid lg:grid-cols-[15rem_minmax(0,1fr)]" data-id="element-192" data-mp="2gdwg7mo2fuox">
      <aside className="hidden border-r border-rule bg-card lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col" data-id="element-193" data-mp="22n1bmmklnzr5">
        <div className="flex flex-col gap-0.5 border-b border-rule px-5 py-4" data-id="element-194" data-mp="26mijfmni18zh">
          <span className="text-heading font-semibold tracking-tight text-ink" data-id="element-195" data-mp="1snsl63oy139j">Portal</span>
          <span className="flex items-center gap-1.5 text-eyebrow-caption font-medium uppercase text-ink-muted" data-id="element-196" data-mp="1xnoffb7u9gbw">
            Hosted by
            <span className="sr-only" data-id="element-197" data-mp="1joygdq1gl6kw">{WORDMARK}</span>
            <Logo size="sm" aria-hidden="true" className="scale-90" data-id="element-198" data-mp="1nopigpwfr94q" />
          </span>
        </div>

        <nav aria-label="Portal" className="flex flex-1 flex-col gap-0.5 p-3" data-id="element-199" data-mp="3e1oe6iuy6zxk">
          {NAV.map(item => {
          const active = item.key === section;
          return <button key={item.key} type="button" onClick={() => onNavigate(item.key)} aria-current={active ? 'page' : undefined} className={`flex min-h-tap flex-col justify-center gap-0.5 rounded-field px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${active ? 'bg-primary-soft' : 'hover:bg-canvas'}`} data-id="element-200" data-mp="uuj8zcqxjcal">
                <span className={`flex items-center gap-2.5 text-body ${active ? 'font-semibold text-primary-text' : 'font-medium text-ink'}`} data-id="element-201" data-mp="19j7096tqszuq">
                  <item.icon aria-hidden="true" className="h-4 w-4" data-id="element-202" data-mp="zsui1l9ehirr" />
                  {item.label}
                </span>
                <span className="pl-[1.625rem] text-small text-ink-muted" data-id="element-203" data-mp="1xb4awulghlu9">{item.hint}</span>
              </button>;
        })}
        </nav>

        <div className="flex flex-col gap-1 border-t border-rule p-3" data-id="element-204" data-mp="1zu92muhbijri">
          <button type="button" onClick={() => onNavigate('profile')} aria-current={section === 'profile' ? 'page' : undefined} className={`flex items-center gap-3 rounded-field px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${section === 'profile' ? 'bg-primary-soft' : 'hover:bg-canvas'}`} data-id="element-205" data-mp="24sj0jcuffygt">
            <InitialAvatar name={CURRENT_MEMBER.fullName} size={36} data-id="element-206" data-mp="2npbs94ny4r91" />
            <span className="flex min-w-0 flex-col" data-id="element-207" data-mp="2sjfzzq51d3b6">
              <span className="truncate text-small font-medium text-ink" data-id="element-208" data-mp="2xh7zp6bx9tpx">
                {CURRENT_MEMBER.publicName}
              </span>
              <span className="text-small text-ink-muted" data-id="element-209" data-mp="31jupz2sy36l0">Your profile</span>
            </span>
          </button>
          <button type="button" onClick={() => onNavigate('profile')} className="flex min-h-tap items-center gap-2.5 rounded-field px-3 text-left text-small text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-210" data-mp="36ifngugtbc5r">
            <SettingsIcon aria-hidden="true" className="h-4 w-4" data-id="element-211" data-mp="2xm5mbkqw0wio" />
            Settings and visibility
          </button>
          <button type="button" onClick={onSignOut} className="flex min-h-tap items-center gap-2.5 rounded-field px-3 text-left text-small text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-212" data-mp="29xcplm2fykn0">
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col" data-id="element-213" data-mp="3v8l9n35zwoe5">
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-rule bg-card px-4 py-2.5 sm:px-6" data-id="element-214" data-mp="3vbh76qdxlb22">
          <span className="text-heading font-semibold tracking-tight text-ink lg:hidden" data-id="element-215" data-mp="1c3200jzxvv36">
            Portal
          </span>

          <label className="relative hidden min-w-0 flex-1 items-center lg:flex" data-id="element-216" data-mp="1h2y40j8mzmiq">
            <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3 h-4 w-4 text-ink-muted" data-id="element-217" data-mp="132nb7anrnxux" />
            <span className="sr-only" data-id="element-218" data-mp="183b27wbzwep5">Search people, years, and the archive</span>
            <input type="search" placeholder="Search people, years, the archive…" className="min-h-tap w-full max-w-md rounded-pill border border-rule bg-canvas pl-9 pr-4 text-small text-ink placeholder:text-ink-subtle focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30" data-id="element-219" data-mp="1vrb1xlrabffv" />
          </label>

          <div className="ml-auto flex items-center gap-2" data-id="element-220" data-mp="2eo2l7v6ttujx">
            <button type="button" className="relative inline-flex h-11 w-11 items-center justify-center rounded-pill text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-221" data-mp="2jgvjq1bpc6nk">
              <span className="sr-only" data-id="element-222" data-mp="2jd4yephg9dwb">Acknowledgements</span>
              <BellIcon aria-hidden="true" className="h-5 w-5" data-id="element-223" data-mp="2ef3ek2c79ajs" />
              <span aria-hidden="true" className="absolute right-2.5 top-2.5 h-2 w-2 rounded-pill bg-accent" data-id="element-224" data-mp="2eetoker5n3e3" />
            </button>
            <span className="lg:hidden" data-id="element-225" data-mp="25lqbw8oc2uki">
              <InitialAvatar name={CURRENT_MEMBER.fullName} size={36} data-id="element-226" data-mp="25kx3z4dn1z0s" />
            </span>
          </div>
        </div>

        <nav aria-label="Portal sections" className="flex gap-1 overflow-x-auto border-b border-rule bg-card px-3 py-2 lg:hidden" data-id="element-227" data-mp="1vou3e2yfq8fy">
          {NAV.map(item => <button key={item.key} type="button" onClick={() => onNavigate(item.key)} aria-current={item.key === section ? 'page' : undefined} className={`inline-flex min-h-tap shrink-0 items-center gap-2 rounded-pill px-3.5 text-small ${item.key === section ? 'bg-primary-soft font-medium text-primary-text' : 'text-ink-muted'}`} data-id="element-228" data-mp="1mnvv38gyrerk">
              <item.icon aria-hidden="true" className="h-4 w-4" data-id="element-229" data-mp="g3a0oysnpg07" />
              {item.label}
            </button>)}
        </nav>

        <main className="flex-1" data-id="element-230" data-mp="3jll5xgli0063">{children}</main>
      </div>
    </div>;
}